import * as React from "react"
import { cmsIntegration as cmsIntegrationText } from "@modules/locales/default.js"
import { CmsOAuthForm } from "./CmsOAuthForm"
import {
  commonMessageTags,
  CommonTagNames,
  FormattedMessage,
} from "@modules/locales"
import { CmsVendor } from "@modules/graphql/types"
import { CmsOAuthFormWithFields } from "./CmsOAuthFormWithFields"
import { Spacer, Text } from "gatsby-interface"
import { OnboardingSiteType } from "@modules/onboarding/shared/types"
import { CmsDataType } from "@modules/cms/shared/utils"
import GatsbyProvisionLearnMoreMessage from "@modules/cms/shared/components/GatsbyProvisionLearnMoreMessage"

export type CmsAuthorizationFormProps = {
  siteId: string
  siteName: string
  siteType: OnboardingSiteType
  cmsVendor: CmsVendor
  cmsVendorName: string
  onComplete: () => void
  onCancel: () => void
  onAuthorize: () => true | undefined
  origin?: `settings` | `createSite`
}

const VENDORS_WITH_PRE_AUTH_INPUT = [CmsVendor.Cosmicjs, CmsVendor.Shopify]

function vendorRequiresPreAuthInput({ vendor }: { vendor: CmsVendor }) {
  return VENDORS_WITH_PRE_AUTH_INPUT.includes(vendor)
}

export function CmsAuthorizationForm({
  siteId,
  siteName,
  siteType,
  cmsVendor,
  cmsVendorName,
  onCancel,
  onComplete,
  onAuthorize,
  origin,
}: CmsAuthorizationFormProps) {
  const isProvisioning = [
    OnboardingSiteType.Provision,
    OnboardingSiteType.DeployNowProvision,
  ].includes(siteType)

  return (
    <React.Fragment>
      {isProvisioning ? (
        <FormattedMessage<"siteName" | "cmsVendor", CommonTagNames>
          message={cmsIntegrationText.messages.authorizationRequiredToProvision}
          values={{
            siteName,
            cmsVendor: cmsVendorName,
          }}
          tags={commonMessageTags}
        />
      ) : (
        <FormattedMessage<"siteName", CommonTagNames>
          message={cmsIntegrationText.messages.authorizationRequired}
          values={{
            siteName,
          }}
          tags={commonMessageTags}
        />
      )}
      {// only render for deploy now provisioning site type and for supported CMS'
      siteType === OnboardingSiteType.DeployNowProvision &&
      (cmsVendor === CmsVendor.Contentful || cmsVendor === CmsVendor.Sanity) ? (
        <>
          <Spacer size={6} />
          <Text size="S">
            <FormattedMessage<"cmsVendor" | "cmsProjectNamePlural">
              message={
                cmsIntegrationText.messages
                  .deployNowGatsbyProvisionHelpTextForCmsVendor
              }
              values={{
                cmsVendor: cmsVendorName,
                cmsProjectNamePlural:
                  cmsVendorName === CmsVendor.Contentful
                    ? `${CmsDataType.ContentfulSpace}s`
                    : `${CmsDataType.SanityDataset}s`,
              }}
              tags={commonMessageTags}
            />
          </Text>
          <Text size="S">
            <GatsbyProvisionLearnMoreMessage cmsVendor={cmsVendor} />
          </Text>
        </>
      ) : null}
      <Spacer size={5} />
      {vendorRequiresPreAuthInput({ vendor: cmsVendor }) ? (
        <CmsOAuthFormWithFields
          siteId={siteId}
          cmsVendor={cmsVendor}
          isProvisioning={isProvisioning}
          onComplete={onComplete}
          onCancel={onCancel}
        />
      ) : (
        <CmsOAuthForm
          siteId={siteId}
          cmsVendor={cmsVendor}
          cmsVendorName={cmsVendorName}
          isProvisioning={isProvisioning}
          onCancel={onCancel}
          onAuthorize={onAuthorize}
          origin={origin}
        />
      )}
    </React.Fragment>
  )
}
